import React, { useEffect } from "react";
import { TextInput, Button, Flex, FlexItem } from "@patternfly/react-core";
import { validateAddressGroup } from "./validators";
import { useTranslation } from "react-i18next";

type ZipCodeInputProps = {
  attr: {
    name: string;
    validators?: any;
  };
  placeholder: string;
  personalInfo: any;
  setPersonalInfo: React.Dispatch<React.SetStateAction<any>>;
  errors: Record<string, string | null>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  autoFillMode: boolean;
  setAutoFillMode: React.Dispatch<React.SetStateAction<boolean>>;
};

declare const AjaxZip3: any;

export const ZipCodeInput = ({
  attr,
  placeholder,
  personalInfo,
  setPersonalInfo,
  errors,
  setErrors,
  autoFillMode,
  setAutoFillMode,
}: ZipCodeInputProps) => {
  const { t } = useTranslation();

  // AjaxZip3 読み込み
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://ajaxzip3.github.io/ajaxzip3.js";
    document.body.appendChild(script);
  }, []);

  // --------------------------------------------------
  // 住所自動入力ボタン押下
  // --------------------------------------------------
  const handleAutoFill = () => {
    const zip = personalInfo.attributes.zip_code?.[0] ?? "";

    if (!zip || zip.length !== 7) return;

    setAutoFillMode(true);

    // 存在しない郵便番号のとき住所をクリア
    AjaxZip3.onFailure = () => {
      setPersonalInfo((prev: any) => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          prefectures: [""],
          municipalities: [""],
          street: [""],
        },
      }));

      setErrors((prev: any) => ({
        ...prev,
        zip_code: null,
        prefectures: null,
        municipalities: null,
        street: null,
      }));

      setAutoFillMode(false);
    };

    // DOMを直接書き換える
    AjaxZip3.zip2addr("zip_code", "", "prefectures", "municipalities", "street");

    // DOMの値をstateに反映
    setTimeout(() => {
      const pref = (document.getElementById("prefectures") as HTMLInputElement)?.value || "";
      const city = (document.getElementById("municipalities") as HTMLInputElement)?.value || "";
      const street = (document.getElementById("street") as HTMLInputElement)?.value || "";

      setPersonalInfo((prev: any) => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          prefectures: [pref],
          municipalities: [city],
          street: [street],
        },
      }));

      setErrors((prev: any) => ({
        ...prev,
        prefectures: null,
        municipalities: null,
        street: null,
      }));

      setAutoFillMode(false);
    }, 50);
  };

  // --------------------------------------------------
  // 手動入力
  // --------------------------------------------------
  const handleChange = (name: string, value: string) => {
    setPersonalInfo((prev: any) => {
      const updated = {
        ...prev,
        attributes: {
          ...prev.attributes,
          [name]: [value],
        },
      };

      if (autoFillMode) {
        setErrors((prev: any) => ({ ...prev, [name]: null }));
        setAutoFillMode(false);
        return updated;
      }

      const correlationErrors = validateAddressGroup(updated.attributes, false);
      setErrors((prev: any) => ({ ...prev, ...correlationErrors }));

      return updated;
    });
  };

  const value = personalInfo.attributes?.[attr.name]?.[0] ?? "";

  return (
    <Flex>
      <FlexItem>
        <TextInput
          id={attr.name}
          name={attr.name}
          placeholder={placeholder}
          value={value}
          onChange={(_, v) => handleChange(attr.name, v)}
          validated={errors[attr.name] ? "error" : "default"}
          maxLength={attr.validators?.length?.max ?? undefined}
        />
      </FlexItem>

      <FlexItem>
        <Button
          onClick={handleAutoFill}
          className={"nc-accountpage-button_addressAutofill"}
        >
          {t("addressAutofill")}
        </Button>
      </FlexItem>
    </Flex>
  );
};